import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import { Menu } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/24/solid";
import { useRegisterCommandPaletteCommands } from "../CommandPalette/commandPaletteRegistration";
import {
  getCommandPaletteKeyShortcuts,
  getCommandPaletteShortcutLabel,
  isApplePlatform,
} from "../CommandPalette/commandPaletteUtils";
import { Modal } from "../Modal";

const ScrollableMenuItems = ({
  open,
  children,
  alignLeft,
  portal,
  buttonReference,
}) => {
  const menuReference = useRef(null);
  const [menuStyle, setMenuStyle] = useState({});

  useLayoutEffect(() => {
    if (!open) return;

    const viewport = window.visualViewport;
    const updateMaximumHeight = () => {
      const viewportBottom = viewport
        ? viewport.offsetTop + viewport.height
        : window.innerHeight;
      if (!portal) {
        const menuTop = menuReference.current.getBoundingClientRect().top;
        setMenuStyle({ maxHeight: Math.max(0, viewportBottom - menuTop - 8) });
        return;
      }

      const viewportLeft = viewport?.offsetLeft ?? 0;
      const viewportTop = viewport?.offsetTop ?? 0;
      const viewportWidth = viewport?.width ?? window.innerWidth;
      const maximumWidth = Math.max(0, viewportWidth - 16);
      const buttonRectangle = buttonReference.current.getBoundingClientRect();
      const minimumWidth = Math.min(buttonRectangle.width, maximumWidth);
      // Measure with the new constraints, not the previous viewport's width.
      menuReference.current.style.maxWidth = `${maximumWidth}px`;
      menuReference.current.style.minWidth = `${minimumWidth}px`;
      const menuWidth = Math.min(
        maximumWidth,
        Math.max(
          buttonRectangle.width,
          menuReference.current.getBoundingClientRect().width,
        ),
      );
      const preferredLeft = alignLeft
        ? buttonRectangle.left
        : buttonRectangle.right - menuWidth;
      const top = Math.max(
        viewportTop + 8,
        Math.min(buttonRectangle.bottom + 8, viewportBottom - 8),
      );
      setMenuStyle({
        left: Math.max(
          viewportLeft + 8,
          Math.min(preferredLeft, viewportLeft + viewportWidth - menuWidth - 8),
        ),
        top,
        minWidth: minimumWidth,
        maxWidth: maximumWidth,
        maxHeight: Math.max(0, viewportBottom - top - 8),
      });
    };

    updateMaximumHeight();
    window.addEventListener("resize", updateMaximumHeight);
    window.addEventListener("scroll", updateMaximumHeight, true);
    viewport?.addEventListener("resize", updateMaximumHeight);
    viewport?.addEventListener("scroll", updateMaximumHeight);

    return () => {
      window.removeEventListener("resize", updateMaximumHeight);
      window.removeEventListener("scroll", updateMaximumHeight, true);
      viewport?.removeEventListener("resize", updateMaximumHeight);
      viewport?.removeEventListener("scroll", updateMaximumHeight);
    };
  }, [open, portal, alignLeft, buttonReference]);

  const items = (
    <Menu.Items
      ref={menuReference}
      style={menuStyle}
      className={`${open ? "block" : "hidden"} ${portal ? "fixed z-40" : "absolute left-0 right-auto z-10 mt-2 min-w-full max-w-[calc(100vw-1rem)]"} w-max origin-top-left divide-y divide-gray-100 overflow-y-auto overscroll-contain rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none ${!portal && !alignLeft ? "lg:left-auto lg:right-0 lg:origin-top-right" : ""}`}
      static
    >
      {children}
    </Menu.Items>
  );

  // Keep modal owners mounted even when the menu is hidden.
  return portal ? createPortal(items, document.body) : items;
};

const MenuButton = props => {
  const {
    label,
    disabled,
    options = [],
    iconOnly = false,
    alignLeft = false,
    portal = false,
    commandPalette = false,
  } = props || {};
  const buttonReference = useRef(null);
  const applePlatform = useMemo(() => isApplePlatform(), []);
  useRegisterCommandPaletteCommands({
    enabled: commandPalette && !disabled,
    options,
    title: label || "Actions",
  });
  const shortcutProps = commandPalette
    ? {
        "aria-keyshortcuts": getCommandPaletteKeyShortcuts(applePlatform),
        title: `${label} (${getCommandPaletteShortcutLabel(applePlatform)})`,
      }
    : {};

  return (
    <Menu
      as="div"
      className="relative inline-block max-w-full flex-shrink-0 text-left"
    >
      {({ open }) => (
        <>
          <div>
            <Menu.Button
              ref={buttonReference}
              disabled={disabled}
              {...shortcutProps}
              className={`flex ${iconOnly ? "" : "w-full"} items-center justify-center whitespace-nowrap rounded-md border border-transparent bg-black
          ${iconOnly ? "h-7 w-7 p-1" : "px-4 py-2"} text-xs font-medium text-white
          ${disabled ? "bg-opacity-70" : "hover:bg-opacity-70"}`}
            >
              {iconOnly ? <span className="sr-only">{label}</span> : label}
              <ChevronDownIcon
                className={`h-4 w-4 ${iconOnly ? "" : "-mr-1 ml-2"}`}
                aria-hidden="true"
              />
            </Menu.Button>
          </div>
          {
            <ScrollableMenuItems
              open={open}
              alignLeft={alignLeft}
              portal={portal}
              buttonReference={buttonReference}
            >
              {options?.map((item, index) => {
                const menuItem = (
                  <Menu.Item disabled={portal ? item?.disabled : undefined}>
                    {({ active }) => {
                      const itemClassName = `${
                        active ? "bg-gray-100 text-black" : "text-gray-900"
                      } ${item?.disabled ? "bg-gray-300" : "hover:bg-gray-50"} group flex w-full truncate whitespace-nowrap rounded-md px-2 py-2 text-left text-sm`;

                      return item?.modalProps && !portal ? (
                        <Modal {...item?.modalProps}>
                          <button
                            title={item?.title || item?.label}
                            disabled={item?.disabled}
                            className={itemClassName}
                          >
                            {item?.label}
                          </button>
                        </Modal>
                      ) : item?.href ? (
                        <a
                          href={item?.disabled ? undefined : item?.href}
                          title={item?.title || item?.label}
                          className={itemClassName}
                          target={item?.target}
                          rel={item?.rel}
                          aria-disabled={item?.disabled ? "true" : "false"}
                          onClick={event => {
                            if (item?.disabled) {
                              event.preventDefault();
                            }
                          }}
                          {...item?.linkProps}
                        >
                          {item?.label}
                        </a>
                      ) : (
                        <button
                          title={item?.title || item?.label}
                          disabled={item?.disabled}
                          className={itemClassName}
                          onClick={item?.disabled ? undefined : item?.onClick}
                        >
                          {item?.label}
                        </button>
                      );
                    }}
                  </Menu.Item>
                );

                return (
                  <div key={index} className="px-1 py-1">
                    {portal && item?.modalProps ? (
                      <Modal {...item.modalProps} disabled={item.disabled}>
                        {menuItem}
                      </Modal>
                    ) : (
                      menuItem
                    )}
                  </div>
                );
              })}
            </ScrollableMenuItems>
          }
          {/* </Transition> */}
        </>
      )}
    </Menu>
  );
};

MenuButton.propTypes = {
  label: PropTypes.string,
  disabled: PropTypes.bool,
  iconOnly: PropTypes.bool,
  alignLeft: PropTypes.bool,
  portal: PropTypes.bool,
  commandPalette: PropTypes.bool,
  options: PropTypes.array,
};

export { MenuButton };
